import React from 'react';
import PropTypes from 'prop-types';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Posts from '../../components/Posts/Posts';
import CTASimple from '../../components/CTASimple/CTASimple';
import theme from '../../styles/theme';
import HeroOverlap from '../../components/HeroOverlap/HeroOverlap';
import ButtonLink from '../../components/Shared/ButtonLink';
import Accordion from '../../components/Accordion/Accordion';
import Error from '../../components/Error/Error';
import { getPageContent } from '../../services/dpc';
import ReactMarkdown from 'react-markdown';
import { slugByLabel } from '../../util/faqCategories';
import useGTM from '../../hooks/useGTM';
import createGTMClass from '../../util/createGTMClass';

const faqGTMLabels = {
  'maximum-fees-charged-for-available-services': 'max_fees',
  'help-finding-the-right-homeowners-insurance': 'insurance_quote',
  'your-initial-tax-escrow-payment-calculation': 'tax_escrow',
};

function FreshStartHomeLoan({
  data: {
    contentCTAs,
    contentVideos,
    faqPod,
    heroCta,
    heroMedia,
    mobileHeroMedia,
    metaDescription,
    preFooterCta,
    tipsAndToolsPod,
    titleTag,
    customerReviewPod,
  } = {},
  error,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const faqs =
    faqPod &&
    faqPod.map(
      ({
        faqQuestion: title,
        faqSummaryAnswer: answer,
        slug,
        faqCategory,
        ...rest
      }) => ({
        title,
        answer,
        showReadMore:
          rest.faqAnswerContent1 ||
          rest.faqAnswerContent2 ||
          rest.faqAnswerVideos,
        slug: slug.replace(
          'resources/faqs/',
          `resources/faqs/${slugByLabel(faqCategory?.[0])}/`
        ),
        onExpand: triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Fresh Start Home Loan',
          category: 'frequently_asked',
          action: 'summary',
          label:
            faqGTMLabels[
              slug.replace('/resources/faqs/', '').replace(/\/$/, '')
            ] ||
            slug
              .replace('/resources/faqs/', '')
              .replace(/-/g, '_')
              .replace(/\/$/, ''),
        }),
        onReadMore: triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Fresh Start Home Loan',
          category: 'frequently_asked',
          action: 'text',
          label:
            faqGTMLabels[
              slug.replace('/resources/faqs/', '').replace(/\/$/, '')
            ] ||
            slug
              .replace('/resources/faqs/', '')
              .replace('-', '_')
              .replace(/\/$/, ''),
        }),
      })
    );

  const posts =
    tipsAndToolsPod &&
    tipsAndToolsPod.map(
      ({ title, contentTag, category, slug, featuredMedia }) => ({
        title,
        category,
        contentTag,
        thumbnail: `${featuredMedia?.media?.fields?.file?.url}?w=300`,
        slug,
      })
    );

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />

      <HeroOverlap
        position='left'
        background={heroMedia?.[0]?.media?.fields?.file?.url}
        mobileBackground={mobileHeroMedia?.[0]?.media?.fields?.file?.url}
        alt={heroMedia?.[0]?.altText}
      >
        <h1>{heroCta?.ctaHeader}</h1>
        <ReactMarkdown source={heroCta?.ctaIntro} />
        <section className='links'>
          <ButtonLink
            appearance='dark'
            to={heroCta?.firstCTAButtonUrl}
            isExternal={true}
            newTab={true}
            size='large'
            className={createGTMClass('apply', 'hero', 'button')}
            onClick={triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Fresh Start Home Loan',
              category: 'loan_application',
              action: 'button',
              label: 'apply',
            })}
          >
            {heroCta?.firstCTAButtonText}
          </ButtonLink>
        </section>
      </HeroOverlap>

      <Accordion
        items={faqs}
        link={{
          url: '/resources/faqs',
          label: 'View all',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Fresh Start Home Loan',
            category: 'frequently_asked',
            action: 'text',
            label: 'faqs',
          }),
        }}
      />

      <CTASimple
        title={contentCTAs?.[0]?.ctaHeader}
        link={{
          title: contentCTAs?.[0].firstCTAButtonText || 'Estimate Payment',
          size: 'large',
          to:
            contentCTAs?.[0]?.firstCTAButtonUrl ||
            '/calculators/mortgage-calculator/',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Fresh Start Home Loan',
            category: 'loan_evaluation',
            action: 'button',
            label: 'mortgage_calc',
          }),
        }}
        condensed={true}
        borders={true}
      >
        <p>{contentCTAs?.[0]?.ctaIntro}</p>
      </CTASimple>

      <Posts
        title='Tips & Tools'
        link={{ title: 'View all', to: '/resources/blog/' }}
        posts={posts}
      />

      <CTASimple
        title={preFooterCta?.ctaHeader || 'We Can Help You Get Started'}
        link={{
          to: preFooterCta?.firstCTAButtonUrl || '/applynow/welcome/',
          title: preFooterCta?.firstCTAButtonText || 'Apply Today',
          size: 'large',
          isExternal: true,
          newTab: true,
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Fresh Start Home Loan',
            category: 'loan_application',
            action: 'button',
            label: 'apply',
          }),
          className: createGTMClass('apply', 'prefoot_cta', 'button'),
        }}
        background={theme.colors.claytonBlue}
      ></CTASimple>
    </Layout>
  );
}

FreshStartHomeLoan.getInitialProps = async ({}) => {
  try {
    const data = await getPageContent('/fresh-start-home-loan/');

    if (data.status !== 200) {
      throw data.statusText;
    }

    return {
      data: data.data.result,
    };
  } catch (error) {
    return {
      error,
    };
  }
};

FreshStartHomeLoan.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default FreshStartHomeLoan;
